import { Component, useEffect, useState } from "react";
import { motion, useMotionValue, useSpring } from "framer-motion";

const HOT_SELECTOR =
  "a, button, [role='button'], [data-cursor='hover'], input, textarea, select, label, [role='slider']";
const PLAY_SELECTOR = "[data-cursor='play']";

const dropNativeHide = () => document.body.classList.remove("has-cursor");

// If the cursor layer ever throws, restore the native pointer immediately.
class CursorBoundary extends Component {
  state = { failed: false };
  static getDerivedStateFromError() {
    return { failed: true };
  }
  componentDidCatch() {
    dropNativeHide();
  }
  render() {
    return this.state.failed ? null : this.props.children;
  }
}

function CursorInner() {
  const [active, setActive] = useState(false);
  const [hot, setHot] = useState(false);
  const [play, setPlay] = useState(false);
  const [visible, setVisible] = useState(false);
  const x = useMotionValue(-100);
  const y = useMotionValue(-100);
  const rx = useSpring(x, { stiffness: 200, damping: 22, mass: 0.55 });
  const ry = useSpring(y, { stiffness: 200, damping: 22, mass: 0.55 });

  useEffect(() => {
    const fine = window.matchMedia("(pointer: fine)").matches;
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (!fine || reduced) return;

    let painted = false;
    const move = (e) => {
      x.set(e.clientX);
      y.set(e.clientY);
      setVisible(true);
      if (!painted) {
        painted = true;
        setActive(true);
        requestAnimationFrame(() => {
          requestAnimationFrame(() => document.body.classList.add("has-cursor"));
        });
      }
    };
    const onTouch = () => {
      window.removeEventListener("mousemove", move);
      dropNativeHide();
      setActive(false);
    };
    const over = (e) => {
      const t = e.target;
      const isPlay = !!t.closest?.(PLAY_SELECTOR);
      setPlay(isPlay);
      setHot(!isPlay && !!t.closest?.(HOT_SELECTOR));
    };
    const leave = () => setVisible(false);

    window.addEventListener("mousemove", move, { passive: true });
    window.addEventListener("touchstart", onTouch, { passive: true });
    document.addEventListener("mouseover", over, true);
    document.documentElement.addEventListener("mouseleave", leave);
    return () => {
      window.removeEventListener("mousemove", move);
      window.removeEventListener("touchstart", onTouch);
      document.removeEventListener("mouseover", over, true);
      document.documentElement.removeEventListener("mouseleave", leave);
      dropNativeHide();
    };
  }, [x, y]);

  if (!active) return null;

  return (
    <div
      className="pointer-events-none fixed inset-0 z-[90]"
      aria-hidden="true"
      data-testid="custom-cursor"
    >
      <motion.div
        className="absolute h-1.5 w-1.5 -translate-x-1/2 -translate-y-1/2 rounded-full bg-gold"
        style={{ left: x, top: y }}
        animate={{ opacity: visible ? 1 : 0, scale: hot || play ? 0.4 : 1 }}
        transition={{ duration: 0.2 }}
      />
      <motion.div
        className="absolute -translate-x-1/2 -translate-y-1/2 rounded-full border"
        style={{ left: rx, top: ry, mixBlendMode: play ? "normal" : "difference" }}
        animate={{
          width: play ? 78 : hot ? 52 : 34,
          height: play ? 78 : hot ? 52 : 34,
          backgroundColor: play ? "rgba(185,154,94,0.18)" : hot ? "rgba(255,255,255,0.92)" : "rgba(255,255,255,0)",
          borderColor: play ? "rgba(185,154,94,0.9)" : "rgba(244,239,231,0.65)",
          opacity: visible ? 1 : 0,
        }}
        transition={{ type: "spring", stiffness: 280, damping: 24 }}
      />
      <motion.span
        className="absolute -translate-x-1/2 -translate-y-1/2 font-mono text-[10px] tracking-[0.24em] text-brass"
        style={{ left: rx, top: ry }}
        animate={{ opacity: play && visible ? 1 : 0 }}
        transition={{ duration: 0.25 }}
      >
        PLAY
      </motion.span>
    </div>
  );
}

export default function Cursor() {
  return (
    <CursorBoundary>
      <CursorInner />
    </CursorBoundary>
  );
}
